import { createClient, SupabaseClient, Session } from '@supabase/supabase-js';
import type { User, Tenant } from './contexts/AuthContext';
import config from './config/environment';

// Use config values directly - these are set from .env via Webpack DefinePlugin at build time
const REACT_APP_SUPABASE_URL = config.REACT_APP_SUPABASE_URL;
const REACT_APP_SUPABASE_KEY = config.REACT_APP_SUPABASE_KEY;

const supabase: SupabaseClient = createClient(REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_KEY);

// Use config for API base URL - no direct process.env access
const getApiBaseUrl = () => {
  return config.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

export const REACT_APP_API_BASE_URL = getApiBaseUrl();

export default REACT_APP_API_BASE_URL;

// Token management
export function setAuthToken(token: string) {
  localStorage.setItem('jwt_token', token);
}

export function getAuthToken(): string | null {
  return localStorage.getItem('jwt_token');
}

export function clearAuthToken() {
  localStorage.removeItem('jwt_token');
}

// Login with Supabase and store JWT
export async function loginWithSupabase(email: string, password: string): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error || !data.session) {
    return { success: false, error: error?.message || 'Login failed' };
  }
  setAuthToken(data.session.access_token);

  // Decode JWT to get fields
  const payload = JSON.parse(atob(data.session.access_token.split('.')[1]));
  if (payload.sub) {
    localStorage.setItem('user_id', payload.sub);
  }
  if (payload.user_role) {
    localStorage.setItem('user_role', payload.user_role);
  }
  if (payload.organization_id) {
    localStorage.setItem('organization_id', payload.organization_id);
  }

  // Store user and tenant/org objects in localStorage using interfaces
  const user: User = {
    id: payload.sub || '',
    email: email,
    name: payload.name || '',
    role: (payload.user_role || 'holder'), // fallback to 'holder' if not present
    tenantId: payload.organization_id || undefined,
    tenantName: payload.organization_name || undefined
  };
  localStorage.setItem('credential-capital-user', JSON.stringify(user));

  if (payload.organization_id) {
    const minimalTenant: Tenant = { id: payload.organization_id, name: '', subdomain: '', primaryColor: '', status: 'active', createdAt: '' };
    localStorage.setItem('credential-capital-tenant', JSON.stringify(minimalTenant));
  } else {
    localStorage.removeItem('credential-capital-tenant');
  }

  return { success: true };
}

// Signup with Supabase invite token
export async function signupWithInvite(token: string, password: string): Promise<{ success: boolean; error?: string }> {
  try {
    // First verify the invite token
    const { data, error: verifyError } = await supabase.auth.verifyOtp({
      token_hash: token,
      type: 'invite'
    });

    if (verifyError || !data.user) {
      return { success: false, error: verifyError?.message || 'Invalid invite token' };
    }

    // Update the user's password
    const { data: updateData, error: updateError } = await supabase.auth.updateUser({
      password: password
    });

    if (updateError || !updateData.user) {
      return { success: false, error: updateError?.message || 'Failed to set password' };
    }

    // Get a fresh session
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !sessionData.session) {
      return { success: false, error: 'Failed to establish session' };
    }

    // Store auth token and user data
    setAuthToken(sessionData.session.access_token);

    // Decode JWT to get user information
    const payload = JSON.parse(atob(sessionData.session.access_token.split('.')[1]));
    
    // Store user metadata
    if (payload.sub) {
      localStorage.setItem('user_id', payload.sub);
    }
    if (payload.user_role) {
      localStorage.setItem('user_role', payload.user_role);
    }
    if (payload.organization_id) {
      localStorage.setItem('organization_id', payload.organization_id);
    }

    // Store user object in localStorage
    const user: User = {
      id: payload.sub || '',
      email: updateData.user.email || '',
      name: payload.name || '',
      role: (payload.user_role || 'holder'),
      tenantId: payload.organization_id || undefined,
      tenantName: payload.organization_name || undefined
    };
    localStorage.setItem('credential-capital-user', JSON.stringify(user));

    // Store tenant info if available
    if (payload.organization_id) {
      const minimalTenant: Tenant = { 
        id: payload.organization_id, 
        name: '', 
        subdomain: '', 
        primaryColor: '', 
        status: 'active', 
        createdAt: '' 
      };
      localStorage.setItem('credential-capital-tenant', JSON.stringify(minimalTenant));
    } else {
      localStorage.removeItem('credential-capital-tenant');
    }

    return { success: true };
  } catch (error: any) {
    console.error('Signup error:', error);
    return { success: false, error: error.message || 'Signup failed' };
  }
}

// Verify invite token without completing signup
export async function verifyInviteToken(token: string): Promise<{ success: boolean; email?: string; error?: string }> {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: token,
      type: 'invite'
    });

    if (error || !data.user) {
      return { success: false, error: error?.message || 'Invalid invite token' };
    }

    return { success: true, email: data.user.email || '' };
  } catch (error: any) {
    console.error('Token verification error:', error);
    return { success: false, error: error.message || 'Token verification failed' };
  }
}

// Verify recovery token
export async function verifyRecoveryToken(token: string): Promise<{ success: boolean; email?: string; error?: string }> {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: token,
      type: 'recovery'
    });

    if (error || !data.user) {
      return { success: false, error: error?.message || 'Invalid or expired recovery token' };
    }

    return { success: true, email: data.user.email || '' };
  } catch (error: any) {
    console.error('Recovery token verification error:', error);
    return { success: false, error: error.message || 'Recovery token verification failed' };
  }
}

// Reset password with recovery token
export async function resetPasswordWithToken(token: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
  try {
    // For access tokens from Supabase recovery links, we don't need to verify
    // Just update the password directly since the token is already verified
    const { data: updateData, error: updateError } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (updateError || !updateData.user) {
      return { success: false, error: updateError?.message || 'Failed to reset password' };
    }

    // Get a fresh session
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !sessionData.session) {
      return { success: false, error: 'Failed to establish session' };
    }

    // Store auth token and user data
    setAuthToken(sessionData.session.access_token);

    // Decode JWT to get user information
    const payload = JSON.parse(atob(sessionData.session.access_token.split('.')[1]));
    
    // Store user metadata
    if (payload.sub) {
      localStorage.setItem('user_id', payload.sub);
    }
    if (payload.user_role) {
      localStorage.setItem('user_role', payload.user_role);
    }
    if (payload.organization_id) {
      localStorage.setItem('organization_id', payload.organization_id);
    }

    // Store user object in localStorage
    const user: User = {
      id: payload.sub || '',
      email: updateData.user.email || '',
      name: payload.name || '',
      role: (payload.user_role || 'holder'),
      tenantId: payload.organization_id || undefined,
      tenantName: payload.organization_name || undefined
    };
    localStorage.setItem('credential-capital-user', JSON.stringify(user));

    // Store tenant info if available
    if (payload.organization_id) {
      const minimalTenant: Tenant = { 
        id: payload.organization_id, 
        name: '', 
        subdomain: '', 
        primaryColor: '', 
        status: 'active', 
        createdAt: '' 
      };
      localStorage.setItem('credential-capital-tenant', JSON.stringify(minimalTenant));
    } else {
      localStorage.removeItem('credential-capital-tenant');
    }

    return { success: true };
  } catch (error: any) {
    console.error('Password reset error:', error);
    return { success: false, error: error.message || 'Password reset failed' };
  }
}

// Authenticated API request
export async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const headers = {
    ...(options.headers || {}),
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  const response = await fetch(`${REACT_APP_API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

export { supabase }; 